import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { successMessages } from '@/constants/feedbackMessages'
import {
	Button,
	ModalBase,
	RadioButton,
	Select,
	TextArea,
} from '@components/atoms'
import { Option } from '@components/atoms/SelectBase/Select'
import { RadioGroup } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { reportErrorSchema } from '@schemas/questions/questions.schema'
import { questionsAPI } from '@services/QuestionsAPI'
import { modalCreators } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@store/hooks'

import { modalErrorReportStyles } from './style'

const {
	Title,
	ContainerTitle,
	Label,
	ModalContainer,
	RedText,
	FormGroup,
	Form,
	Info,
	radioGroup,
	ButtonsContainer,
	WarningContainer,
	WarningRedText,
} = modalErrorReportStyles

export const ModalQuestionErrorReport = () => {
	const {
		modal: { modalName, modalInfo },
	} = useAppSelector((state) => state)
	const dispatch = useAppDispatch()
	const [createQuestionReportError, { isLoading, isSuccess }] =
		questionsAPI.usePostReportErrorQuestionMutation()

	const [errorType, setErrorType] = useState<Option>()
	const [explanationWrong, setExplanationWrong] = useState<boolean>(false)
	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isValid },
	} = useForm<{
		message: string
	}>({ resolver: yupResolver(reportErrorSchema), mode: 'onChange' })

	const onSubmitComment = handleSubmit((payload) => {
		createQuestionReportError({
			questionId: modalInfo.questionId,
			message: payload.message,
			answerWrong: explanationWrong,
			errorType: errorType?.value.toString(),
			appVersion: 'Web',
			osVersion: 'Web',
		})

		dispatch(notificationCreators.show(successMessages.createErrorContent))
		dispatch(modalCreators.closeModal())
	})

	useEffect(() => {
		if (isSuccess) {
			reset()
			setErrorType({ value: '', option: '' })
			dispatch(notificationCreators.show(successMessages.createErrorContent))
		}
	}, [dispatch, isSuccess, reset])

	return (
		<ModalBase
			title=''
			open={modalName === 'questionErrorReport'}
			variant='questionErrorReport'
			maxWidth='780px'
		>
			<ModalContainer>
				<ContainerTitle>
					<img
						src='/assets/images/exclamationIcon.png'
						alt={modalInfo?.title}
					/>
					{modalInfo?.title && <Title>{modalInfo.title}</Title>}
				</ContainerTitle>
				{!modalInfo?.noShowInfo && (
					<Form>
						<FormGroup>
							<Label>
								Selecione o tipo de erro<RedText>*</RedText>
							</Label>
							<Select
								onChange={setErrorType}
								placeholder='Selecione o tipo de erro'
								options={[
									{ option: 'Erro de explicação', value: 'answer_error' },
									{ option: 'Erro de titulo', value: 'title_error' },
									{ option: 'Erro de enunciado', value: 'description_error' },
								]}
								selectedValue={errorType?.value}
							/>
						</FormGroup>
						<FormGroup>
							<Label>
								O gabarito da questão está de acordo com o oficial da banca?
								<RedText>*</RedText>
							</Label>
							<RadioGroup
								value={explanationWrong}
								onChange={setExplanationWrong}
								className={radioGroup()}
							>
								<RadioGroup.Option value>
									{({ checked }) => (
										<RadioButton
											customText='Sim'
											questionType
											checked={checked}
											questionAnswer={false}
											isCorrect
										/>
									)}
								</RadioGroup.Option>
								<RadioGroup.Option value={false}>
									{({ checked }) => (
										<RadioButton
											customText='Não'
											questionType
											checked={checked}
											questionAnswer={false}
											isCorrect
										/>
									)}
								</RadioGroup.Option>
							</RadioGroup>
						</FormGroup>
						<FormGroup>
							<Label>
								Justifique de forma fundamentada o motivo do gabarito estar
								incorreto.<RedText>*</RedText>
							</Label>
							<TextArea
								placeholder='Descreva brevemente sobre o que está acontecendo...'
								css={{ borderColor: '$neutral50', minHeight: '92px' }}
								error={errors.message?.message}
								{...register('message')}
							/>
						</FormGroup>
						<WarningContainer>
							<WarningRedText>Atenção:</WarningRedText>
							<Info>
								Nós agradecemos imensamente pelo auxílio em aprimorar nosso
								portal. No entanto, pedimos encarecidamente que enviem apenas
								reports que estejam efetivamente em desacordo com os gabaritos
								oficiais das bancas examinadoras. Fixamos esta mensagem pois,
								atualmente 1 a cada 100 reports são procedentes, o que demanda
								um elevado número de horas de análise das questões reportadas
								por parte da nossa equipe. Desde já agradecemos pelo auxílio e
								compreensão.
							</Info>
						</WarningContainer>

						<ButtonsContainer>
							<Button
								onClick={onSubmitComment}
								disabled={isLoading || errorType?.value === '' || !isValid}
								icon={modalInfo?.button?.icon}
								color='danger'
								css={{
									minHeight: '48px',
									width: '340px',
									textAlign: 'center',
									_font: 'medium',
									fontWeight: 500,

									'&:disabled': {
										backgroundColor: 'grey',
										borderColor: '$disabled',
										color: '$disabled',
									},
								}}
							>
								Reportar
							</Button>
							<Button
								onClick={() => {
									reset()
									dispatch(modalCreators.closeModal())
									setErrorType({ value: '', option: '' })
								}}
								outlined
								icon={modalInfo?.button?.icon}
								color='secondary'
								css={{
									minHeight: '48px',
									width: '340px',
									textAlign: 'center',
									_font: 'medium',
									fontWeight: 500,
								}}
							>
								Cancelar
							</Button>
						</ButtonsContainer>
					</Form>
				)}
			</ModalContainer>
		</ModalBase>
	)
}
